import React, {
   ChangeEvent,
   CSSProperties,
   FocusEvent,
   forwardRef,
   ReactElement,
   ReactNode,
   Ref, useCallback,
   useContext,
   useImperativeHandle,
   useRef
} from 'react';
import { defined } from '@unifocus/js-utils';
import { convertSize } from '../../../../utils/StyleUtils';
import { FormContext, generateFormFieldId } from '../../form';
import CloseCircleFilled from '@ant-design/icons/CloseCircleFilled';

/**
 * {@docCategory Input}
 */
export interface InputProps {
   /**
    * The name of the input item.
    */
   name?: string;

   /**
    * The value.
    */
   value?: string;

   /**
    * The placeholder text for the input item.
    */
   placeholder?: string;

   /**
    * Sets the input item to read-only.
    * @defaultvalue false
    */
   readOnly?: boolean;

   /**
    * Set the input item to disabled.
    * @defaultvalue false
    */
   disabled?: boolean;

   /**
    * Determines if a border should be shown around the component.
    * @defaultvalue true
    */
   border?: boolean;

   /**
    * Determines if the component should have internal padding.
    * @defaultvalue true
    */
   padding?: boolean;

   /**
    * The type of the input component.
    * @defaultvalue text
    */
   type?: string;

   /**
    * Hint for which keyboard to show on mobile devices.
    * @defaultvalue text
    */
   inputMode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';

   /**
    * Hint for how to handle automatic capitalization.
    * @defaultvalue sentences
    */
   autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';

   /**
    * Hint for how to handle automatic completion.
    * https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
    */
   autoComplete?: string | 'off' | 'on';

   /**
    * Sets the maximum number of characters that can be entered.
    */
   maxLength?: number;

   /**
    * Enable the clear feature.
    * @defaultvalue false
    */
   allowClear?: boolean;

   /**
    * The prefix element.
    */
   prefix?: ReactNode;

   /**
    * The suffix element.
    */
   suffix?: ReactNode;

   /**
    * The label text displayed before (on the left side of) the input field.
    */
   addonBefore?: ReactNode;

   /**
    * The label text displayed after (on the right side of) the input field.
    */
   addonAfter?: ReactNode;

   /**
    * Sets the popupTrigger.
    */
   popupTrigger?: ReactElement;

   /**
    * Sets the width.
    */
   width?: string | number;

   /**
    * Sets the min width.
    */
   minWidth?: string | number;

   /**
    * Sets the max width.
    */
   maxWidth?: string | number;

   /**
    * Set this to focus on form item on first render
    */
   autoFocus?: boolean;

   /**
    * The pattern specifies a regular expression that the <input> element's value is checked against
    * as the user types
    */
   inputKeyFilter?: string | RegExp | ((value: string) => boolean);

   /**
    * Whether or not to trigger change events on blur
    * @defaultvalue false
    */
   triggerOnBlur?: boolean;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;

   /**
    * Callback called when the value is changed.
    * @param value - The value
    */
   onChange?: (value?: string) => void;

   /**
    * Callback called when the component loses focus.
    * @param event - The focus event object
    */
   onBlur?: (event: FocusEvent<HTMLInputElement>) => void;

   /**
    * Callback called when the component gains focus.
    * @param event - The focus event object
    */
   onFocus?: (event: FocusEvent<HTMLInputElement>) => void;

   /**
    * Function called when the user presses the enter key.
    */
   onPressEnter?: React.KeyboardEventHandler<HTMLInputElement>;

   /**
    * Function called when the user presses a key.
    */
   onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
}

export interface InputRefType {
   value: string;
   focus: () => void;
   blur: () => void;
   select: () => void;
   setRangeText(replacement: string): void;
   setSelectionRange: (start: number, end: number, direction?: "forward" | "backward" | "none") => void;
   addEventListener: <K extends keyof HTMLElementEventMap>(type: K, listener: (this: HTMLInputElement, ev: HTMLElementEventMap[K]) => void, options?: boolean | AddEventListenerOptions) => void;
   removeEventListener: <K extends keyof HTMLElementEventMap>(type: K, listener: (this: HTMLInputElement, ev: HTMLElementEventMap[K]) => void, options?: boolean | EventListenerOptions) => void;
}

const ENTER_KEY_CODE = 13;

const handleKeyDown = (props: InputProps) => (event: React.KeyboardEvent<HTMLInputElement>) => {
   const { onPressEnter, onKeyDown } = props;

   if (event.keyCode === ENTER_KEY_CODE && onPressEnter) {
      onPressEnter(event);
   }

   if (onKeyDown) {
      onKeyDown(event);
   }
};

export const Input = forwardRef<InputRefType, InputProps>(
   (props: InputProps, ref: Ref<InputRefType>) => {

   const inputRef = useRef<HTMLInputElement | null>(null);
   const value = defined(props.value) ? props.value : '';
   const showClear = props.allowClear && value;
   const showBorder = props.border === undefined ? true : props.border;
   const padding = props.padding === undefined ? true : props.padding;
   const classNames = ['uf-input'];
   const { onChange, onBlur, triggerOnBlur, onFocus, disabled, readOnly, inputKeyFilter } = props;

   const style = {
      width: convertSize(props.width, '250px'),
      minWidth: convertSize(props.minWidth, '60px'),
      maxWidth: convertSize(props.maxWidth),
      ...props.style
   };

   if (showBorder) {
      classNames.push('uf-input-bordered');
   }

   if (props.className) {
      classNames.push(props.className);
   }

   if (props.disabled) {
      classNames.push('uf-input-disabled');
   }

   const matchesMaskPattern = useCallback((value: string) => {
      let match;
      if (typeof inputKeyFilter === 'string') {
         match = new RegExp(inputKeyFilter).exec(value);
      }
      else if (typeof inputKeyFilter === 'function') {
         match = inputKeyFilter(value);
      }
      else {
         match = inputKeyFilter?.exec(value);
      }
      return match;
   },[inputKeyFilter]);

   const onChangeHandler = useCallback((event: ChangeEvent<HTMLInputElement>) => {
      if (onChange && !triggerOnBlur) {
         const value = event.target.value.trim().length === 0 ? undefined : event.target.value;

         if (inputKeyFilter && value) {
            if (matchesMaskPattern(value)) {
               onChange(value);
            }
         }
         else {
            onChange(value);
         }
      }
   }, [onChange, triggerOnBlur, inputKeyFilter, matchesMaskPattern]);

   const onBlurHandler = useCallback((event: FocusEvent<HTMLInputElement>) => {

      const cancelButtonCausedBlur = (event.relatedTarget && (event.relatedTarget as HTMLElement).getAttribute('data-iscancelbutton'));

      if (!cancelButtonCausedBlur && triggerOnBlur && onChange) {
         const value = event.target.value.trim().length === 0 ? undefined : event.target.value;

         if (inputKeyFilter && value) {
            if (matchesMaskPattern(value)) {
               onChange(value);
            }
         }
         else {
            onChange(value);
         }
      }

      if (onBlur) {
         onBlur(event);
      }
   }, [onChange, onBlur, triggerOnBlur, inputKeyFilter, matchesMaskPattern]);

   const onFocusHandler = useCallback((event: FocusEvent<HTMLInputElement>) => {
      if (onFocus) {
         onFocus(event);
      }
   },[onFocus]);

   const onClear = useCallback(() => {
      if (disabled || readOnly) {
         return;
      }

      if (onChange) {
         onChange(undefined);
      }

      if (inputRef.current) {
         inputRef.current.focus();
      }
   },[disabled, readOnly, onChange]);

   useImperativeHandle(ref, () => ({
      value: (inputRef.current) ? inputRef.current.value : undefined,
      focus: () => {
         if (inputRef.current) {
            inputRef.current.focus();
         }
      },
      blur: () => {
         if (inputRef.current) {
            inputRef.current.blur();
         }
      },
      select: () => {
         if (inputRef.current) {
            inputRef.current.select();
         }
      },
      setRangeText: (replacement: string) => {
         if (inputRef.current) {
            inputRef.current.setRangeText(replacement);
            inputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
         }
      },
      setSelectionRange: (selectionStart: number, selectionEnd: number, selectionDirection?: 'forward' | 'backward' | 'none') => {
         if (inputRef.current) {
            inputRef.current.setSelectionRange(selectionStart, selectionEnd, selectionDirection);
         }
      },
      addEventListener: <K extends keyof HTMLElementEventMap>(type: K, listener: (this: HTMLInputElement, ev: HTMLElementEventMap[K]) => void, options?: boolean | AddEventListenerOptions) => {
         if (inputRef.current) {
            inputRef.current.addEventListener(type, listener, options);
         }
      },
      removeEventListener: <K extends keyof HTMLElementEventMap>(type: K, listener: (this: HTMLInputElement, ev: HTMLElementEventMap[K]) => void, options?: boolean | EventListenerOptions) => {
         if (inputRef.current) {
            inputRef.current.removeEventListener(type, listener, options);
         }
      }
   } as InputRefType));

   const formContext = useContext(FormContext);
   const inputId = generateFormFieldId(formContext.name, props.name);

   return (
      <span
         className={classNames.join(' ')}
         style={style}
      >
         <span className={'uf-input-wrapper'}>
            {props.addonBefore && <span className={'uf-input-addon'}>
               {props.addonBefore}
            </span>}

            <span className={`uf-input-control-container ${padding ? 'uf-input-control-container-padding' : ''}`}>
               {props.prefix && <span className={`uf-input-prefix ${props.disabled ? 'uf-input-prefix-disabled' : ''} ${props.readOnly ? 'uf-input-prefix-readonly' : ''}`}>
                  {props.prefix}
               </span>}

               <input
                  id={inputId}
                  ref={inputRef}
                  name={props.name}
                  type={props.type || 'text'}
                  inputMode={props.inputMode || 'text'}
                  autoCapitalize={props.autoCapitalize}
                  autoComplete={props.autoComplete || 'off'}
                  placeholder={props.placeholder}
                  readOnly={props.readOnly}
                  disabled={props.disabled}
                  maxLength={props.maxLength}
                  className={'uf-input-control'}
                  value={value}
                  autoFocus={props.autoFocus}
                  onChange={onChangeHandler}
                  onBlur={onBlurHandler}
                  onFocus={onFocusHandler}
                  onKeyDown={handleKeyDown(props)}
               />

               {showClear && !props.disabled && !props.readOnly && <span className="uf-input-clear" onClick={onClear} onFocus={(event) => {event.preventDefault()}}>
                  <CloseCircleFilled/>
               </span>}

               {props.popupTrigger && <span className={`uf-input-popup-trigger ${props.disabled ? 'uf-input-popup-trigger-disabled' : ''} ${props.readOnly ? 'uf-input-popup-trigger-readonly' : ''}`}>
                  {props.popupTrigger}
               </span>}

               {props.suffix && <span className={`uf-input-suffix ${props.disabled ? 'uf-input-suffix-disabled' : ''} ${props.readOnly ? 'uf-input-suffix-readonly' : ''}`} >
                  {props.suffix}
               </span>}
            </span>

            {props.addonAfter && <span className={'uf-input-addon'}>
               {props.addonAfter}
            </span>}
         </span>
      </span>
   );
});
